// Offline cache service using localStorage
// CREATED: Simple cache system for offline mode support
// Features: TTL support, user-specific cache, date-based keys
// CHANGE: Removed all debug console statements for production

import { AssignedTaskWithDetails } from './execution';

const CACHE_VERSION = '1.0';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

interface UserCache {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

// Generate cache key
function getCacheKey(userId: string, date: string): string {
  return `tasq_tasks_${userId}_${date}`;
}

function getUserCacheKey(userId: string): string {
  return `tasq_user_${userId}`;
}

// Check if cache entry is valid (not expired)
function isValidCache<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
  if (!entry) return false;
  if (entry.version !== CACHE_VERSION) return false;

  const now = Date.now();
  const age = now - entry.timestamp;

  return age < CACHE_TTL;
}

// Cache tasks for a specific date
export function cacheTasksForDate(
  userId: string,
  date: string,
  tasks: AssignedTaskWithDetails[]
): void {
  try {
    const key = getCacheKey(userId, date);
    const entry: CacheEntry<AssignedTaskWithDetails[]> = {
      data: tasks,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };

    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
  }
}

// Get cached tasks for a specific date
export function getCachedTasksForDate(
  userId: string,
  date: string
): AssignedTaskWithDetails[] | null {
  try {
    const key = getCacheKey(userId, date);
    const cached = localStorage.getItem(key);

    if (!cached) return null;

    const entry: CacheEntry<AssignedTaskWithDetails[]> = JSON.parse(cached);

    if (!isValidCache(entry)) {
      // Clean up expired cache
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    return null;
  }
}

// Cache user information
export function cacheUserInfo(userId: string, userInfo: UserCache): void {
  try {
    const key = getUserCacheKey(userId);
    const entry: CacheEntry<UserCache> = {
      data: userInfo,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };

    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
  }
}

// Get cached user information
export function getCachedUserInfo(userId: string): UserCache | null {
  try {
    const key = getUserCacheKey(userId);
    const cached = localStorage.getItem(key);

    if (!cached) return null;

    const entry: CacheEntry<UserCache> = JSON.parse(cached);

    if (!isValidCache(entry)) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    return null;
  }
}

// Clear all cache for a user
export function clearUserCache(userId: string): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(`tasq_tasks_${userId}_`) || key.startsWith(`tasq_user_${userId}`)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
  }
}

// Clear expired cache entries (maintenance)
export function clearExpiredCache(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('tasq_')) {
        const cached = localStorage.getItem(key);
        if (cached) {
          try {
            const entry = JSON.parse(cached);
            if (!isValidCache(entry)) {
              localStorage.removeItem(key);
            }
          } catch {
            // Invalid JSON, remove it
            localStorage.removeItem(key);
          }
        }
      }
    });
  } catch (error) {
  }
}
